import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'

import frontCardImage from 'assets/images/WayniCard.png'
import { ClosedEye, OpenEye } from 'assets/icons'

import { getUrlSensitiveData } from 'api/credit-card'
import { sendOTP } from 'api/OTP'
import { useCardIframeUrl, useDataToggleStore } from 'store'
import { Card } from 'types/api/card'

const CreditCard = ({
  disabled = true,
  loading,
  setLoading,
  card
}: {
  disabled: boolean
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  card: Card
}) => {
  const { virtualCardIsHidden, physicalCardIsHidden, updateIsHidden } =
    useDataToggleStore()
  const {
    updateUuid,
    getUrlWithExpirationDate,
    updatePhysicalCardUrl,
    updateVirtualCardUrl,
    physicalCardUrl,
    virtualCardUrl
  } = useCardIframeUrl()

  const { card_type: cardType, uuid, card_status: cardStatus } = card

  const setter =
    cardType === 'VIRTUAL' ? updateVirtualCardUrl : updatePhysicalCardUrl
  const isHidden =
    cardType === 'VIRTUAL' ? virtualCardIsHidden : physicalCardIsHidden

  useEffect(() => {
    const cardIframe = cardType === 'VIRTUAL' ? virtualCardUrl : physicalCardUrl

    if (!cardIframe && !isHidden) {
      updateIsHidden({ state: true, type: cardType })
    }
  }, [virtualCardUrl, physicalCardUrl])

  const navigate = useNavigate()

  const dataToggle = () => {
    // hide data
    if (!isHidden) return updateIsHidden({ state: true, type: cardType })

    if (getUrlWithExpirationDate(cardType))
      return updateIsHidden({ state: false, type: cardType })

    sendOTP().then(({ success, statusCode }) => {
      // Chequeo de OTP si está activado o no
      if (statusCode === 417) return getUrl()

      if (!success) return navigate('/pedir-adelanto/error-otp')

      updateUuid(uuid)
      navigate(`/mis-tarjetas/mostrar-datos/${cardType}`)
    })
  }

  const getUrl = () => {
    setLoading(true)

    getUrlSensitiveData({ uuid })
      .then((data) => setter(data))
      .catch(() => {
        updateIsHidden({ state: true, type: cardType })
        setter(null)
      })
      .finally(() => setLoading(false))
  }

  return (
    <div className='w-full'>
      {loading ? (
        <div className='w-full h-full'>
          <Skeleton height={192} className='rounded-lg' />
        </div>
      ) : (
        <div className='relative overflow-hidden w-auto'>
          <img
            className={`${disabled && 'opacity-50'}`}
            src={frontCardImage}
            alt=''
          />
          {!isHidden && cardStatus === 'ACTIVE' ? (
            <iframe
              title='Información de la tarjeta'
              src={getUrlWithExpirationDate(cardType)}
              className='w-full aspect-[4/3] absolute top-0'
            />
          ) : null}
        </div>
      )}
      {!disabled && (
        <button
          type='button'
          className={`flex gap-1 mt-2 pr-1 justify-end w-full items-center font-bold text-purple-900 text-xs ${
            loading && 'opacity-50'
          }`}
          onClick={() => dataToggle()}
          disabled={loading}
        >
          {isHidden ? (
            <OpenEye className='w-5' stroke='#442E83' />
          ) : (
            <ClosedEye className='w-5' stroke='#442E83' />
          )}
          {isHidden ? 'Mostrar datos' : 'Ocultar datos'}
        </button>
      )}
    </div>
  )
}

export default CreditCard
